// src/stores/workflowTasksStore.js
import { defineStore } from 'pinia'
import { ref, computed } from 'vue'
import { collection, query, orderBy } from 'firebase/firestore'
import { onSnapshot } from '@/services/sessionListeners'
import { db } from '@/services/firebaseconfig'

/**
 * A small set of manual overrides for attributes that live outside the task's own uiProps.
 * Example: Schedule Site Survey (date/status under siteSurveySchedule.* on the customer).
 * You can add more here as needed.
 */
const OVERRIDES = {
  // Schedule Site Survey
  // taskId: 'fwURVuoG8muvc04TZlSF'
  fwURVuoG8muvc04TZlSF: {
    labelOverride: 'Schedule Site Survey',
    attributes: [
      {
        key: 'completion',
        label: 'Completion',
        type: 'completion',
        operators: ['is_complete', 'is_not_complete'],
      },
      {
        key: 'date',
        label: 'Date',
        type: 'date',
        operators: ['is_before', 'is_on', 'is_after'],
        path: 'siteSurveySchedule.scheduledDateTime',
      },
      {
        key: 'status',
        label: 'Status',
        type: 'select',
        operators: ['is', 'is_not'],
        path: 'siteSurveySchedule.status',
        getOptions: () => [
          { value: 'Pending', label: 'Pending' },
          { value: 'Approved', label: 'Approved' },
          { value: 'Rejected', label: 'Rejected' },
        ],
      },
    ],
  },
}

export const useWorkflowTasksStore = defineStore('workflowTasks', () => {
  const allTasks = ref([])
  const isLoading = ref(false)
  const hasFetched = ref(false)
  let _unsubscribe = null

  // Phase 33B: Real-time subscription — onSnapshot with IndexedDB persistence
  // gives instant cached data then syncs network deltas.
  function subscribe(options = {}) {
    const force = options.force === true
    if (force && _unsubscribe) {
      _unsubscribe()
      _unsubscribe = null
      hasFetched.value = false
    }
    if (_unsubscribe) return // Already subscribed
    isLoading.value = true
    const perfLabel = `[PERF] [workflowTasksStore] subscribe`
    console.time(perfLabel)
    _unsubscribe = onSnapshot(
      query(collection(db, 'workflowTasks'), orderBy('name')),
      snap => {
        allTasks.value = snap.docs.map(doc => ({ id: doc.id, ...doc.data() }))
        if (!hasFetched.value) console.timeEnd(perfLabel)
        hasFetched.value = true
        isLoading.value = false
      },
      error => {
        console.error('[workflowTasksStore] onSnapshot error:', error)
        isLoading.value = false
      }
    )
  }

  function unsubscribeAll() {
    if (_unsubscribe) {
      _unsubscribe()
      _unsubscribe = null
    }
    hasFetched.value = false
    allTasks.value = []
  }

  // Backward-compatible alias — existing callers (DashboardPage, NotificationWizard, etc.)
  // call fetchTasks() which now delegates to subscribe().
  function fetchTasks(options = {}) {
    subscribe(options)
  }

  /**
   * Phase 18: Infer attributes from inputSchema.
   * Falls back to inferAttributesFromUiProps for legacy tasks without inputSchema.
   */
  function inferAttributesFromSchema(task) {
    const schema = task.inputSchema
    if (!schema) return inferAttributesFromUiProps(task)

    const attrs = [
      {
        key: 'completion',
        label: 'Completion',
        type: 'completion',
        operators: ['is_complete', 'is_not_complete'],
      },
    ]

    if (schema.type === 'object') {
      const fields = schema.constraints?.object?.schema?.fields || []
      for (const field of fields) {
        if (field.type === 'choice') {
          const normOptions = (field.data?.staticOptions || [])
            .filter(o => o && o.id)
            .map(o => ({ value: o.id, label: o.label || o.id }))
          attrs.push({
            key: field.id,
            label: field.label,
            type: 'select',
            operators: ['is', 'is_not'],
            getOptions: () => normOptions,
          })
        }
      }
    } else if (schema.type === 'choice') {
      const normOptions = (schema.data?.staticOptions || [])
        .filter(o => o && o.id)
        .map(o => ({ value: o.id, label: o.label || o.id }))
      attrs.push({
        key: 'value',
        label: schema.label || 'Value',
        type: 'select',
        operators: ['is', 'is_not'],
        getOptions: () => normOptions,
      })
    } else if (schema.type === 'boolean') {
      attrs.push({
        key: 'value',
        label: schema.label || 'Value',
        type: 'select',
        operators: ['is', 'is_not'],
        getOptions: () => [
          { value: 'true', label: schema.constraints?.boolean?.trueLabel || 'Yes' },
          { value: 'false', label: schema.constraints?.boolean?.falseLabel || 'No' },
        ],
      })
    } else if (schema.type === 'datetime') {
      attrs.push({
        key: 'date',
        label: schema.label || 'Date',
        type: 'date',
        operators: ['is_before', 'is_on', 'is_after'],
      })
    }

    return attrs
  }

  /**
   * Legacy: Infer a normalized attribute list for a given task doc from its uiProps.
   * Produces entries like:
   *  - completion: { type:'completion', operators:['is_complete','is_not_complete'], flagPath?: string }
   *  - status:     { type:'select',     operators:['is','is_not'], path:'<flagPath>', getOptions() }
   *  - signature:  { type:'select',     operators:['is','is_not'], path:'<signatureFlagPath>', getOptions() }
   */
  function inferAttributesFromUiProps(task) {
    const attrs = []

    // 1) Completion
    // Prefer explicit flagPath on the workflow (uiProps.flagPath).
    // If absent, we'll still allow completion via the task's activeWorkflow.tasks[].isComplete boolean (handled in evaluator).
    attrs.push({
      key: 'completion',
      label: 'Completion',
      type: 'completion',
      operators: ['is_complete', 'is_not_complete'],
      flagPath: task?.uiProps?.flagPath || null,
    })

    // 2) Status dropdown
    if (task?.uiProps?.hasStatusDropdown && task?.uiProps?.statusFlagPath) {
      const optionsArr =
        Array.isArray(task?.uiProps?.options) && task.uiProps.options.length
          ? task.uiProps.options
          : Array.isArray(task?.uiProps?.documentStatusOptions)
            ? task.uiProps.documentStatusOptions
            : []
      const normOptions = optionsArr
        .filter(o => o && o.value != null)
        .map(o => ({ value: String(o.value), label: o.label || String(o.value) }))

      attrs.push({
        key: 'status',
        label: 'Status',
        type: 'select',
        operators: ['is', 'is_not'],
        path: task.uiProps.statusFlagPath,
        getOptions: () => normOptions,
      })
    }

    // 3) Signature status dropdown
    if (task?.uiProps?.hasSignatureStatus && task?.uiProps?.signatureFlagPath) {
      const sigOptions = Array.isArray(task?.uiProps?.signatureOptions)
        ? task.uiProps.signatureOptions
        : []
      const normSigOptions = sigOptions
        .filter(o => o && o.value != null)
        .map(o => ({ value: String(o.value), label: o.label || String(o.value) }))

      attrs.push({
        key: 'signature',
        label: 'Signature',
        type: 'select',
        operators: ['is', 'is_not'],
        path: task.uiProps.signatureFlagPath,
        getOptions: () => normSigOptions,
      })
    }

    return attrs
  }

  /**
   * A computed map of taskId → inferred attribute definition list.
   * Merge with OVERRIDES (OVERRIDES win).
   */
  const taskAttributeMap = computed(() => {
    const perfLabel = `[PERF] [workflowTasksStore] computed:taskAttributeMap`
    console.time(perfLabel)
    const map = {}
    for (const t of allTasks.value) {
      const base = {
        labelOverride:
          t?.inputSchema?.label || t?.uiProps?.displayName || t?.displayName || t?.name || '',
        attributes: inferAttributesFromSchema(t),
      }
      map[t.id] = base
    }
    // Merge overrides
    for (const [taskId, override] of Object.entries(OVERRIDES)) {
      map[taskId] = {
        labelOverride: override.labelOverride || map[taskId]?.labelOverride,
        attributes: Array.isArray(override.attributes)
          ? override.attributes
          : map[taskId]?.attributes || [],
      }
    }
    console.timeEnd(perfLabel)
    return map
  })

  /**
   * Public helper: get attribute definitions for a task id.
   */
  function attributesForTask(taskId) {
    const def = taskAttributeMap.value[taskId]
    if (!def) {
      // Default to completion-only if unknown
      return [
        {
          key: 'completion',
          label: 'Completion',
          type: 'completion',
          operators: ['is_complete', 'is_not_complete'],
        },
      ]
    }
    return def.attributes?.length
      ? def.attributes
      : [
          {
            key: 'completion',
            label: 'Completion',
            type: 'completion',
            operators: ['is_complete', 'is_not_complete'],
          },
        ]
  }

  /**
   * Public helper: readable label override for task (for sentence builder).
   */
  function labelForTask(taskId) {
    return taskAttributeMap.value[taskId]?.labelOverride || ''
  }

  const taskMap = computed(() => {
    const map = {}
    for (const t of allTasks.value) {
      map[t.id] = t
    }
    return map
  })

  function getTaskById(taskId) {
    return taskMap.value[taskId] || null
  }

  return {
    allTasks,
    isLoading,
    hasFetched,
    fetchTasks,
    subscribe,
    unsubscribeAll,
    taskAttributeMap,
    attributesForTask,
    labelForTask,
    taskMap,
    getTaskById,
  }
})
